/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOOLTIP_UPDATES_INVENTORY_FILTERS
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Added tooltips to Clear Filters and Collapse/Expand buttons.
 *
 * File Size Check:
 * - Current: 634 lines (OVERSIZED)
 * - Adding: ~10 lines
 * - Projected: 644 lines
 * - Decision: Proceed (minimal change, split needed separately)
 *
 * Related Files:
 * - client/src/constants/tooltips.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    EXTRACT_INVENTORY_FILTERS
 * Date:         2025-12-11
 * Modified By:  Cursor
 *
 * Description:
 * Extracted filter sidebar from InventoryPage.tsx into reusable component
 * for use in both personal inventory and global search pages.
 *
 * Related Screens / Modules:
 * - client/src/components/inventory/InventoryFilters.tsx
 * - client/src/features/inventory/InventoryPage.tsx
 */

import React, { useMemo } from "react";
import { Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faSliders } from "@fortawesome/free-solid-svg-icons";
import { Inventory } from "../../interfaces/Inventory";
import { ACTION_BUTTON_TOOLTIPS } from "../../constants/tooltips";
import "./InventoryFilters.scss";

// [2025-12-11] Filters: Reusable filter component interface
interface InventoryFiltersProps {
  // Filter values
  searchQuery: string;
  partNumberFilter: string;
  categoryFilter: string;
  conditionFilter: string;
  countryFilter: string;
  manufacturerFilter: string;
  minPriceFilter: number;
  maxPriceFilter: number;
  qtyFilter?: number; // QTY - quantity filter
  companyFilter?: string; // Company filter

  // Change handlers
  onSearchChange: (value: string) => void;
  onPartNumberChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onConditionChange: (value: string) => void;
  onCountryChange: (value: string) => void;
  onManufacturerChange: (value: string) => void;
  onMinPriceChange: (value: number) => void;
  onMaxPriceChange: (value: number) => void;
  onQtyChange?: (value: number) => void;
  onCompanyChange?: (value: string) => void;
  onClearFilters: () => void;

  // Optional: Hide/show specific filters
  showPriceFilter?: boolean;
  showQtyFilter?: boolean;
  showCompanyFilter?: boolean;

  // Items to extract dropdown options from (all fetched items for cascading dropdowns)
  allInventoryItems?: Inventory[]; // Required - all items for dynamic dropdown extraction

  // Optional: Collapse state
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

// Utility functions to extract unique values from inventory items
const extractUniqueCategories = (items: Inventory[]): string[] => {
  const categories = new Set<string>();
  items.forEach((item) => {
    if (item.category && item.category.trim()) {
      categories.add(item.category.trim());
    }
  });
  return Array.from(categories).sort();
};

const extractUniqueManufacturers = (items: Inventory[]): string[] => {
  const manufacturers = new Set<string>();
  items.forEach((item) => {
    if (item.manufacturer && item.manufacturer.trim()) {
      manufacturers.add(item.manufacturer.trim());
    }
  });
  return Array.from(manufacturers).sort();
};

const extractUniqueConditions = (items: Inventory[]): string[] => {
  const conditions = new Set<string>();
  items.forEach((item) => {
    if (item.condition && item.condition.trim()) {
      conditions.add(item.condition.trim());
    }
  });
  return Array.from(conditions).sort();
};

const extractUniqueQuantities = (items: Inventory[]): number[] => {
  const quantities = new Set<number>();
  items.forEach((item) => {
    if (item.quantity && item.quantity > 0) {
      quantities.add(item.quantity);
    }
  });
  return Array.from(quantities).sort((a, b) => a - b);
};

const extractUniqueCountries = (items: Inventory[]): string[] => {
  const countries = new Set<string>();
  items.forEach((item) => {
    // Country comes from sellerId when populated (becomes an object with country field)
    // Check if sellerId is an object (populated) with country
    // Note: typeof null === "object" in JavaScript, so we need to check for null explicitly
    if (
      item.sellerId &&
      typeof item.sellerId === "object" &&
      item.sellerId !== null &&
      !Array.isArray(item.sellerId)
    ) {
      const sellerIdObj = item.sellerId as any;
      if (
        sellerIdObj.country &&
        typeof sellerIdObj.country === "string" &&
        sellerIdObj.country.trim()
      ) {
        countries.add(sellerIdObj.country.trim());
      }
    }
    // Also check seller object (alternative populated field name)
    else if (
      item.seller &&
      typeof item.seller === "object" &&
      item.seller !== null &&
      !Array.isArray(item.seller) &&
      (item.seller as any).country
    ) {
      const country = (item.seller as any).country;
      if (country && typeof country === "string" && country.trim()) {
        countries.add(country.trim());
      }
    }
  });
  return Array.from(countries).sort();
};

const extractUniqueCompanies = (items: Inventory[]): string[] => {
  const companies = new Set<string>();
  items.forEach((item) => {
    // Company name comes from sellerId when populated
    // Note: typeof null === "object" in JavaScript, so we need to check for null explicitly
    if (
      item.sellerId &&
      typeof item.sellerId === "object" &&
      item.sellerId !== null &&
      !Array.isArray(item.sellerId)
    ) {
      const sellerIdObj = item.sellerId as any;
      if (
        sellerIdObj.companyName &&
        typeof sellerIdObj.companyName === "string" &&
        sellerIdObj.companyName.trim()
      ) {
        companies.add(sellerIdObj.companyName.trim());
      }
    }
    // Also check seller object (alternative populated field name)
    else if (
      item.seller &&
      typeof item.seller === "object" &&
      item.seller !== null &&
      !Array.isArray(item.seller) &&
      (item.seller as any).companyName
    ) {
      const companyName = (item.seller as any).companyName;
      if (companyName && typeof companyName === "string" && companyName.trim()) {
        companies.add(companyName.trim());
      }
    }
  });
  return Array.from(companies).sort();
};

const extractUniquePartNumbers = (items: Inventory[]): string[] => {
  const partNumbers = new Set<string>();
  items.forEach((item) => {
    if (item.partNumber && item.partNumber.trim()) {
      partNumbers.add(item.partNumber.trim());
    }
  });
  return Array.from(partNumbers).sort();
};

// Helper function to filter items excluding one specific field
// This allows each dropdown to show all available options when that field is changed
const filterExcludingField = (
  items: Inventory[],
  excludeField: string,
  filters: {
    searchQuery: string;
    partNumberFilter: string;
    categoryFilter: string;
    conditionFilter: string;
    countryFilter: string;
    manufacturerFilter: string;
    qtyFilter: number;
    companyFilter: string;
  },
): Inventory[] => {
  return items.filter((item) => {
    // Search query filter (keyword) - always apply unless this is the excluded field
    // Since searchQuery is not a dropdown field, we always apply it
    if (filters.searchQuery && filters.searchQuery.trim()) {
      const keyword = filters.searchQuery.trim().toLowerCase();
      const matchesKeyword =
        (item.title && item.title.toLowerCase().includes(keyword)) ||
        (item.manufacturer && item.manufacturer.toLowerCase().includes(keyword)) ||
        (item.model && item.model.toLowerCase().includes(keyword)) ||
        (item.partNumber && item.partNumber.toLowerCase().includes(keyword)) ||
        (item.description && item.description.toLowerCase().includes(keyword));
      if (!matchesKeyword) return false;
    }

    // Part Number filter (skip if this is the excluded field)
    if (excludeField !== "partNumber" && filters.partNumberFilter) {
      const matchesPartNumber = item.partNumber && item.partNumber.trim() === filters.partNumberFilter;
      if (!matchesPartNumber) return false;
    }

    // Category filter (skip if this is the excluded field)
    if (excludeField !== "category" && filters.categoryFilter) {
      if (!item.category || item.category.trim() !== filters.categoryFilter) {
        return false;
      }
    }

    // Condition filter (skip if this is the excluded field)
    if (excludeField !== "condition" && filters.conditionFilter) {
      if (!item.condition || item.condition.trim() !== filters.conditionFilter) {
        return false;
      }
    }

    // Manufacturer filter (skip if this is the excluded field)
    if (excludeField !== "manufacturer" && filters.manufacturerFilter) {
      if (!item.manufacturer || item.manufacturer.trim() !== filters.manufacturerFilter) {
        return false;
      }
    }

    // Quantity filter (skip if this is the excluded field)
    if (excludeField !== "qty" && filters.qtyFilter > 0) {
      if (item.quantity !== filters.qtyFilter) return false;
    }

    // Country filter (skip if this is the excluded field)
    if (excludeField !== "country" && filters.countryFilter) {
      let itemCountry: string | undefined;
      // Note: typeof null === "object" in JavaScript, so we need to check for null explicitly
      if (
        item.sellerId &&
        typeof item.sellerId === "object" &&
        item.sellerId !== null &&
        !Array.isArray(item.sellerId)
      ) {
        const sellerIdObj = item.sellerId as any;
        itemCountry = sellerIdObj.country;
      } else if (
        item.seller &&
        typeof item.seller === "object" &&
        item.seller !== null &&
        !Array.isArray(item.seller) &&
        (item.seller as any).country
      ) {
        itemCountry = (item.seller as any).country;
      }
      if (!itemCountry || itemCountry.trim() !== filters.countryFilter.trim()) {
        return false;
      }
    }

    // Company filter (skip if this is the excluded field)
    if (excludeField !== "company" && filters.companyFilter) {
      let itemCompanyName: string | undefined;
      // Note: typeof null === "object" in JavaScript, so we need to check for null explicitly
      if (
        item.sellerId &&
        typeof item.sellerId === "object" &&
        item.sellerId !== null &&
        !Array.isArray(item.sellerId)
      ) {
        const sellerIdObj = item.sellerId as any;
        itemCompanyName = sellerIdObj.companyName;
      } else if (
        item.seller &&
        typeof item.seller === "object" &&
        item.seller !== null &&
        !Array.isArray(item.seller) &&
        (item.seller as any).companyName
      ) {
        itemCompanyName = (item.seller as any).companyName;
      }
      if (!itemCompanyName || itemCompanyName.trim() !== filters.companyFilter.trim()) {
        return false;
      }
    }

    return true;
  });
};

// [2025-12-11] Filters: Main filter component
export function InventoryFilters({
  searchQuery,
  partNumberFilter,
  categoryFilter,
  conditionFilter,
  countryFilter,
  manufacturerFilter,
  minPriceFilter,
  maxPriceFilter,
  qtyFilter = 0,
  companyFilter = "",
  onSearchChange,
  onPartNumberChange,
  onCategoryChange,
  onConditionChange,
  onCountryChange,
  onManufacturerChange,
  onMinPriceChange,
  onMaxPriceChange,
  onQtyChange,
  onCompanyChange,
  onClearFilters,
  showPriceFilter = true,
  showQtyFilter = false,
  showCompanyFilter = false,
  allInventoryItems = [],
  collapsed = false,
  onToggleCollapse,
}: InventoryFiltersProps) {
  // Filter state object for helper function (memoized to prevent unnecessary re-renders)
  const filterState = useMemo(
    () => ({
      searchQuery,
      partNumberFilter,
      categoryFilter,
      conditionFilter,
      countryFilter,
      manufacturerFilter,
      qtyFilter,
      companyFilter,
    }),
    [
      searchQuery,
      partNumberFilter,
      categoryFilter,
      conditionFilter,
      countryFilter,
      manufacturerFilter,
      qtyFilter,
      companyFilter,
    ],
  );

  // For each dropdown, extract from items filtered by all OTHER filters
  // This allows the user to change their selection in that field
  // Note: searchQuery is always applied to filter items before extracting dropdown options
  const itemsForPartNumber = useMemo(
    () => filterExcludingField(allInventoryItems, "partNumber", filterState),
    [allInventoryItems, filterState],
  );
  const itemsForCategory = useMemo(
    () => filterExcludingField(allInventoryItems, "category", filterState),
    [allInventoryItems, filterState],
  );
  const itemsForCondition = useMemo(
    () => filterExcludingField(allInventoryItems, "condition", filterState),
    [allInventoryItems, filterState],
  );
  const itemsForManufacturer = useMemo(
    () => filterExcludingField(allInventoryItems, "manufacturer", filterState),
    [allInventoryItems, filterState],
  );
  const itemsForQty = useMemo(
    () => filterExcludingField(allInventoryItems, "qty", filterState),
    [allInventoryItems, filterState],
  );
  const itemsForCountry = useMemo(
    () => filterExcludingField(allInventoryItems, "country", filterState),
    [allInventoryItems, filterState],
  );
  const itemsForCompany = useMemo(
    () => filterExcludingField(allInventoryItems, "company", filterState),
    [allInventoryItems, filterState],
  );

  // Extract unique values from appropriately filtered items (memoized for performance)
  const uniquePartNumbers = useMemo(() => extractUniquePartNumbers(itemsForPartNumber), [itemsForPartNumber]);
  const uniqueCategories = useMemo(
    () => extractUniqueCategories(itemsForCategory),
    [itemsForCategory],
  );
  const uniqueConditions = useMemo(
    () => extractUniqueConditions(itemsForCondition),
    [itemsForCondition],
  );
  const uniqueManufacturers = useMemo(
    () => extractUniqueManufacturers(itemsForManufacturer),
    [itemsForManufacturer],
  );
  const uniqueQuantities = useMemo(() => extractUniqueQuantities(itemsForQty), [itemsForQty]);
  const uniqueCountries = useMemo(() => extractUniqueCountries(itemsForCountry), [itemsForCountry]);
  const uniqueCompanies = useMemo(() => extractUniqueCompanies(itemsForCompany), [itemsForCompany]);
  return (
    <div className="inventory-filters">
      <div
        className={`${
          collapsed
            ? "d-flex justify-content-center"
            : "d-flex justify-content-between align-items-center"
        } mb-3`}
      >
        {!collapsed && <h5 className="m-0 filter-title">Filters</h5>}
        {onToggleCollapse && (
          <button
            className={`btn-figma-1 btn-sm filter-collapse-btn ${collapsed ? "filter-collapse-btn--collapsed" : ""}`}
            onClick={onToggleCollapse}
            aria-label={collapsed ? "Expand filters" : "Collapse filters"}
            title={collapsed ? ACTION_BUTTON_TOOLTIPS.EXPAND_FILTERS : ACTION_BUTTON_TOOLTIPS.COLLAPSE_FILTERS}
          >
            {collapsed ? <FontAwesomeIcon icon={faSliders} /> : <FontAwesomeIcon icon={faTimes} />}
          </button>
        )}
      </div>

      {/* Filter content - only show when not collapsed */}
      <div className={`filter-content ${collapsed ? "d-none" : ""}`}>
        {!collapsed && (
          <>
            {/* Search Input - Part Number, Keyword, Manufacturer */}
            <Form.Group className="mb-3 filter-first-group">
              <Form.Label htmlFor="search" className="filter-label">
                keyword
              </Form.Label>
              <Form.Control
                type="text"
                id="search"
                className="filter-input"
                placeholder="Input"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </Form.Group>

            {/* Part Number Filter */}
            <Form.Group className="mb-3">
              <Form.Label htmlFor="partNumber" className="filter-label">
                Part Number
              </Form.Label>
              <Form.Select
                id="partNumber"
                className="filter-input"
                value={partNumberFilter}
                onChange={(e) => onPartNumberChange(e.target.value)}
              >
                <option value="">All Part Numbers</option>
                {uniquePartNumbers.map((partNumber) => (
                  <option key={partNumber} value={partNumber}>
                    {partNumber}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Category Filter */}
            <Form.Group className="mb-3">
              <Form.Label htmlFor="category" className="filter-label">
                Category
              </Form.Label>
              <Form.Select
                id="category"
                className="filter-input"
                value={categoryFilter}
                onChange={(e) => onCategoryChange(e.target.value)}
              >
                <option value="">All Categories</option>
                {uniqueCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* QTY Filter */}
            {showQtyFilter && onQtyChange && (
              <Form.Group className="mb-3">
                <Form.Label htmlFor="qty" className="filter-label">
                  QTY - quantity
                </Form.Label>
                <Form.Select
                  id="qty"
                  className="filter-input"
                  value={qtyFilter || ""}
                  onChange={(e) => onQtyChange(parseInt(e.target.value) || 0)}
                >
                  <option value="">All Quantities</option>
                  {uniqueQuantities.map((qty) => (
                    <option key={qty} value={qty}>
                      {qty}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {/* Condition Filter */}
            <Form.Group className="mb-3">
              <Form.Label htmlFor="condition" className="filter-label">
                Condition
              </Form.Label>
              <Form.Select
                id="condition"
                className="filter-input"
                value={conditionFilter}
                onChange={(e) => onConditionChange(e.target.value)}
              >
                <option value="">All</option>
                {uniqueConditions.map((cond) => (
                  <option key={cond} value={cond}>
                    {cond}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Country Filter */}
            <Form.Group className="mb-3">
              <Form.Label htmlFor="country" className="filter-label">
                Country
              </Form.Label>
              <Form.Select
                id="country"
                className="filter-input"
                value={countryFilter}
                onChange={(e) => onCountryChange(e.target.value)}
              >
                <option value="">All</option>
                {uniqueCountries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Manufacturer Filter */}
            <Form.Group className="mb-3">
              <Form.Label htmlFor="manufacturer" className="filter-label">
                Manufacturer
              </Form.Label>
              <Form.Select
                id="manufacturer"
                className="filter-input"
                value={manufacturerFilter}
                onChange={(e) => onManufacturerChange(e.target.value)}
              >
                <option value="">All Manufacturers</option>
                {uniqueManufacturers.map((mfr) => (
                  <option key={mfr} value={mfr}>
                    {mfr}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Company Filter */}
            {showCompanyFilter && onCompanyChange && (
              <Form.Group className="mb-3">
                <Form.Label htmlFor="company" className="filter-label">
                  Company
                </Form.Label>
                <Form.Select
                  id="company"
                  className="filter-input"
                  value={companyFilter}
                  onChange={(e) => onCompanyChange(e.target.value)}
                >
                  <option value="">Company</option>
                  {uniqueCompanies.map((company) => (
                    <option key={company} value={company}>
                      {company}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {/* Price Range Filter */}
            {showPriceFilter && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="minPrice">Min Price</Form.Label>
                  <Form.Control
                    type="number"
                    id="minPrice"
                    min="0"
                    step="0.01"
                    value={minPriceFilter || ""}
                    onChange={(e) => onMinPriceChange(parseFloat(e.target.value) || 0)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label htmlFor="maxPrice">Max Price</Form.Label>
                  <Form.Control
                    type="number"
                    id="maxPrice"
                    min="0"
                    step="0.01"
                    value={maxPriceFilter || ""}
                    onChange={(e) => onMaxPriceChange(parseFloat(e.target.value) || 0)}
                  />
                </Form.Group>
              </>
            )}

            {/* Clear filters */}
            <button
              className="btn-cancel-figma w-100"
              onClick={onClearFilters}
              title={ACTION_BUTTON_TOOLTIPS.CLEAR_FILTERS}
            >
              Clear All Filters
            </button>
          </>
        )}
      </div>
    </div>
  );
}
